// packages/frontend/src/app/providers/ProductsProvider.tsx
import React, { createContext, useContext, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getProducts, getProductDetail } from '../../services/apiMedia';

type Product = {
  productID: string;
  title: string;
  price?: string;
  thumbnailUrl?: string;
  owned?: boolean;
  publicCount?: number;
  privateCount?: number;
};

type ProductsCtx = {
  products: Product[];
  isLoading: boolean;
  error?: string;
  refetch: () => Promise<any>;
  getProductDetail: (productId: string) => Promise<any>;
};

const ProductsContext = createContext<ProductsCtx | undefined>(undefined);

export default function ProductsProvider({ children }: { children: React.ReactNode }) {
  const qc = useQueryClient();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['products', 'public'],
    queryFn: () => getProducts(), // your /media/api/public/products
    staleTime: 60_000,
  });

  const value = useMemo<ProductsCtx>(() => ({
    products: Array.isArray(data) ? data : [],
    isLoading,
    error: error ? (error as any).message ?? 'Failed to load products' : undefined,
    refetch,
    getProductDetail: async (productId: string) => {
      const qk = ['productDetail', productId];
      const existing = qc.getQueryData(qk);
      if (existing) return existing;
      const detail = await qc.fetchQuery({
        queryKey: qk,
        queryFn: () => getProductDetail(productId),
        staleTime: 60_000,
      });
      return detail;
    },
  }), [data, isLoading, error, refetch, qc]);

  return <ProductsContext.Provider value={value}>{children}</ProductsContext.Provider>;
}

export function useProducts() {
  const ctx = useContext(ProductsContext);
  if (!ctx) throw new Error('useProducts must be used within <ProductsProvider>');
  return ctx;
}
